// modeResolver.js — m3-06a two-mode shell logic (pure, dependency-free, node-testable)
//
// The Agent Experience is ONE app with two layouts:
//   - 'standard' : the normal Vesta SaaS site (gradient nav + content area).
//   - 'agent'    : the Codex-desktop "Agent Studio" (chat + resizable right panel that
//                  mounts the SAME Activity surface, menu-less).
//
// The Standard|AI-Agent toggle (m3-10 nav) drives the mode by navigating to
// `/activity?mode=agent` (server-rendered HAML link). The shell reads the resolved mode
// from the page's `data-mode` attribute, with a localStorage fallback for continuity so a
// refresh / back-forward keeps the chosen mode. This module is the single source of truth
// for "which layout am I in", so both the gate and the React shell agree.
//
// NO React, NO DOM globals required at import time — every function takes its inputs
// explicitly so it runs headless under node (the §NEW-UI pure-logic gate).

export const MODES = Object.freeze(["standard", "agent"])
export const DEFAULT_MODE = "standard"
export const MODE_STORAGE_KEY = "vesta-agent-mode"

// Activity tab persistence (lifted to the shell so the choice survives mode switches and the
// site view + agent panel mirror each other — DESIGN-INTEGRATION §2.2, docs/01).
export const ACTIVITY_VIEWS = Object.freeze(["board", "agents"]) // 'agents' label = "Triggered Tasks"
export const DEFAULT_ACTIVITY_VIEW = "board"
export const ACTIVITY_VIEW_STORAGE_KEY = "vesta-activity-view"

// Resolve the effective mode from (in priority order):
//   1. an explicit `?mode=` value baked into the page (server-rendered data-mode), if valid
//   2. the persisted localStorage value, if valid
//   3. DEFAULT_MODE
//
// `dataMode`  — the string from the page's data-mode attribute (may be null/undefined).
// `stored`    — the string read from localStorage (may be null/undefined).
export function resolveMode(dataMode, stored) {
  if (isValidMode(dataMode)) return dataMode
  if (isValidMode(stored)) return stored
  return DEFAULT_MODE
}

export function isValidMode(m) {
  return MODES.indexOf(m) !== -1
}

// Resolve the SHELL mode for the side-nav lower section (VsNavLower).
//
// The server (HAML) is authoritative: nav.sidebar.vs-nav carries the data-agent-mode
// attribute on explicit mode-aware URLs such as /activity?mode=agent and
// /artifacts?mode=agent. When that server-rendered nav is present in the DOM, its attribute
// decides the mode OUTRIGHT — present → 'agent', absent → 'standard'.
// A stale localStorage MODE_KEY (e.g. left "agent" after an EXIT that intentionally skips
// persisting "standard") must NEVER resurface the agent chat list on a Standard page.
// localStorage is consulted ONLY when the nav is absent (SSR / tests / non-shell pages),
// as a continuity fallback. This differs from resolveMode(), whose `dataMode` is a string
// that may legitimately be null on a page that still wants the persisted value; here the
// ABSENCE of the attribute is itself the server saying "standard", not "unknown".
export function resolveShellMode({ navPresent, agentAttrPresent, stored }) {
  if (navPresent) return agentAttrPresent ? "agent" : DEFAULT_MODE
  return isValidMode(stored) ? stored : DEFAULT_MODE
}

export function isAgentMode(mode) {
  return mode === "agent"
}

// The toggle is symmetric: it always shows where you are and offers the other mode.
export function otherMode(mode) {
  return mode === "agent" ? "standard" : "agent"
}

// The URL a mode toggle should navigate to. Standard = the app root for the company;
// Agent = the Activity destination in agent mode (the panel mounts Activity).
export function toggleHref(targetMode) {
  return targetMode === "agent" ? "/activity?mode=agent" : "/activity"
}

// Activity view resolution — same priority shape as mode.
export function resolveActivityView(requested, stored) {
  if (isValidActivityView(requested)) return requested
  if (isValidActivityView(stored)) return stored
  return DEFAULT_ACTIVITY_VIEW
}

export function isValidActivityView(v) {
  return ACTIVITY_VIEWS.indexOf(v) !== -1
}

// Persistence helpers — guarded so they no-op without a real Storage (node / SSR).
// NEVER clear keys we didn't write (docs/07 rule).
export function persistMode(mode, storage) {
  if (!storage || !isValidMode(mode)) return false
  try { storage.setItem(MODE_STORAGE_KEY, mode); return true } catch (_e) { return false }
}

export function readPersistedMode(storage) {
  if (!storage) return null
  try { return storage.getItem(MODE_STORAGE_KEY) } catch (_e) { return null }
}

export function persistActivityView(view, storage) {
  if (!storage || !isValidActivityView(view)) return false
  try { storage.setItem(ACTIVITY_VIEW_STORAGE_KEY, view); return true } catch (_e) { return false }
}

export function readPersistedActivityView(storage) {
  if (!storage) return null
  try { return storage.getItem(ACTIVITY_VIEW_STORAGE_KEY) } catch (_e) { return null }
}
